import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useForm } from '@weezevent/weezjs-core';
import { requiredField } from '../../../../../utils/validators';

import { ModerationTransitions } from '../../../../../models';

import { Card, WeezHeader, Button, ToastContext, HeaderCard, TitleCard, SubTitleCard, ContentCard } from '@weezevent/nacre';

import { SettingsStep, ActionsStep, OperatorsStep, ConditionsStep } from './steps';
import LoadingCard from '../../../../../components/loadingCard';

export default function TransitionDetails({ organization, flowId, transitionId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const transitionExists = React.useMemo(() => !!transitionId, [transitionId]);

    const validators = React.useMemo(() => {
        return {
            source_status: requiredField,
            destination_status: requiredField
        };
    }, []);

    const [form, setForm, setFormObject] = useForm(
        {
            name: '',
            source_status: null,
            destination_status: null,
            actions: [],
            conditions: [],
            allowed_users: [],
            id: null,
            display: true,
            is_automatic: false
        },
        validators
    );

    const [, loadingTransition] = ModerationTransitions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: transitionId,
            launch: Boolean(transitionId),
            cache: false,
            allow_cache: false,
            onSuccess: ({ object }) => {
                setFormObject({
                    ...form,
                    ...object,
                    destination_status: object.destination_status.id,
                    source_status: object.source_status.id,
                    allowed_users: object.allowed_users.map(user => parseInt(user.id))
                });
            }
        },
        [organization, flowId, transitionId]
    );

    const steps = React.useMemo(
        () => [
            {
                key: 'settings',
                title: t('ticket.moderation.statusesTransitions.transition.steps.settings.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.settings.subtitle'),
                summary: `${t('ticket.moderation.statusesTransitions.transition.steps.settings.summary')} ${form.name}`,
                component: (
                    <SettingsStep
                        form={form}
                        flowId={flowId}
                        setForm={setForm}
                        organization={organization}
                        onDone={onCardDone => onCardDone()}
                        transitionExists={transitionExists}
                    />
                )
            },
            {
                key: 'actions',
                title: t('ticket.moderation.statusesTransitions.transition.steps.actions.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.actions.subtitle'),
                summary: (
                    <span>
                        {t('ticket.moderation.statusesTransitions.transition.steps.actions.summary')}
                        {form.actions.length}
                    </span>
                ),
                component: (
                    <ActionsStep
                        form={form}
                        flowId={flowId}
                        setForm={setForm}
                        organization={organization}
                        transitionId={transitionId}
                        onDone={onCardDone => onCardDone()}
                        transitionExists={transitionExists}
                    />
                )
            },
            {
                key: 'conditions',
                title: t('ticket.moderation.statusesTransitions.transition.steps.conditions.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.conditions.subtitle'),
                summary: (
                    <span>
                        {t('ticket.moderation.statusesTransitions.transition.steps.conditions.summary')}
                        {form.conditions?.length}
                    </span>
                ),
                component: (
                    <ConditionsStep
                        form={form}
                        flowId={flowId}
                        setForm={setForm}
                        organization={organization}
                        transitionId={transitionId}
                        onDone={onCardDone => onCardDone()}
                        transitionExists={transitionExists}
                    />
                )
            },
            {
                key: 'operators',
                title: t('ticket.moderation.statusesTransitions.transition.steps.operators.title'),
                subtitle: t('ticket.moderation.statusesTransitions.transition.steps.operators.subtitle'),
                component: <OperatorsStep organizationId={organization.id} flowId={flowId} form={form} setForm={setForm} transitionExists={transitionExists} />
            }
        ],
        [form, organization, flowId, transitionExists]
    );

    const handleSubmit = React.useCallback(() => {
        let transition = new ModerationTransitions({ ...form });
        transition.actions = form.actions.map(cond => cond.id);
        transition.conditions = form.conditions.map(cond => cond.id);
        transition
            .save({ organizationId: organization.id, flowId })
            .then(() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`))
            .catch(() => toast.error(t('ticket.moderation.statusesTransitions.transition.steps.messageErrorSave')));
    }, [form, organization.id, flowId]);

    return (
        <>
            <WeezHeader
                title={t(`ticket.moderation.statusesTransitions.transition.${transitionExists ? 'editTitle' : 'createTitle'}`)}
                rightComponent={transitionExists ? [<Button key={0} primary label={t('ticket.common.action.save')} onClick={() => handleSubmit()} />] : []}
                backLink={{
                    onClick: () => {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
                    }
                }}
            />
            {transitionExists ? <TransitionPage steps={steps} loading={loadingTransition} /> : <TransitionSteps steps={steps} />}
        </>
    );
}

const TransitionSteps = ({ steps }) => {
    const { t } = useTranslation();
    return (
        <Card.FormManager labelPreviousStepMobile={t('ticket.common.back_step')} labelStepMobile={t(['ticket.common.step.current'])} withStepsMobile>
            {steps.map(step => (
                <Card.FormStep
                    key={step.key}
                    title={step.title}
                    summary={step.summary}
                    subtitle={step.subtitle}
                    editLabel={t('ticket.common.action.edit')}
                    onEdit={step.onEdit ? () => step.onEdit() : undefined}
                >
                    {step.component}
                </Card.FormStep>
            ))}
        </Card.FormManager>
    );
};

const TransitionPage = ({ steps, loading }) => {
    return steps.map(step => (
        <Card key={step.key}>
            <HeaderCard separator>
                <TitleCard title={step.title} />
                <SubTitleCard title={step.subtitle} />
            </HeaderCard>
            <LoadingCard loading={loading}>
                <ContentCard>{step.component}</ContentCard>
            </LoadingCard>
        </Card>
    ));
};
